import type { QueryClient } from '@tanstack/react-query';
import { clearSegmentOutIntegrationCache } from '../segment-out-integration/cache';
import { segmentOutIntegrationKeys } from '../segment-out-integration/entity';
import { SEGMENT_OUT_LEGACY_ID, segmentOutLegacyKeys } from './entity';

/**
 * Imperative cache ops for the legacy Segment source record.
 *
 * Two slots hold this one row, and both matter here:
 *
 *  - **this** slot, which the legacy form reads for its key and its event map;
 *  - the `segment-out-integration` detail slot, whose `use_javascript` is what
 *    chooses between the two forms in the first place.
 *
 * So a write here has to move both, unlike the sibling connectors that own a
 * single slot. Leaving the second one behind is not a stale badge, it is the page
 * rendering the wrong form: after a remove it would still say a legacy record
 * exists and keep showing this form for a record that is gone.
 *
 * The integrations directory and the connections overview read a third slot,
 * `statusKey(workspaceId, 'segdotcom')` in `services/db/integration`. That one is
 * deliberately left alone, as it is for every other connector — it carries a 60s
 * `staleTime` and refreshes on its own.
 */
const detailKey = (workspaceId: number) => segmentOutLegacyKeys.detail(workspaceId, SEGMENT_OUT_LEGACY_ID);

/**
 * Re-read both records after a save.
 *
 * The saved state is fetched rather than written from the response: the PUT
 * echoes the body it was given, so it cannot say what the record now holds —
 * the API stores the events under an `email_` prefix and answers the GET from
 * the full catalogue.
 */
export async function refreshSegmentOutLegacyCache(qc: QueryClient, workspaceId: number): Promise<void> {
  await Promise.all([
    qc.invalidateQueries({ queryKey: detailKey(workspaceId) }),
    qc.invalidateQueries({ queryKey: segmentOutIntegrationKeys.detail(workspaceId, 'segment-out') }),
  ]);
}

/**
 * A removed connector is "connected: no" in both slots, which is `null` rather
 * than an absent slot — the same reasoning as `clearSegmentOutIntegrationCache`:
 * observers settle on one answer instead of refetching a record that is gone.
 */
export function clearSegmentOutLegacyCache(qc: QueryClient, workspaceId: number): void {
  qc.setQueryData(detailKey(workspaceId), null);
  clearSegmentOutIntegrationCache(qc, workspaceId);
}
